import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Upload, Download, BookOpen, Check, Printer } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { toast } from '../hooks/use-toast';

// Restauración de páginas de cómic escaneadas. Corre en el propio servidor
// (OpenCV) — sin créditos, sin proveedor — así que este diálogo no abre un
// workflow de lienzo: sube/elige la página, procesa, y el resultado queda en
// Creations como cualquier otra imagen.
//
// Una subida nueva NO pasa por la biblioteca de assets: los assets se
// reescalan a 1280px al entrar, y una restauración para imprimir no puede
// permitirse tirar píxeles antes de empezar. El fichero original viaja
// entero por /comic/process-upload.
const MODES = [
  { key: 'restore', label: 'Restore', hint: 'Clean the paper, keep everything' },
  { key: 'modernize', label: 'Modernize', hint: 'Flat modern colour, same ink lines' },
];

export default function ComicRestore({ onClose }) {
  const fileRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState('');
  const [file, setFile] = useState(null);       // a fresh scan, kept at full resolution
  const [filePreview, setFilePreview] = useState(null);
  const [mode, setMode] = useState('restore');
  const [printSize, setPrintSize] = useState(true);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // {url, printUrl?, meta}

  useEffect(() => {
    api.listAssets()
      .then((a) => setAssets((a || []).filter((x) => x.type !== 'style')))
      .catch((e) => toast({ title: 'Could not load your images', description: e.message }));
  }, []);

  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 40 * 1024 * 1024) {
      toast({ title: 'That scan is too big', description: 'The limit is 40MB. Export it as JPEG and try again.' });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
    setAssetId('');
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const run = async () => {
    if ((!file && !assetId) || working) return;
    setWorking(true);
    setResult(null);
    try {
      const r = file
        ? await api.comicProcessUpload(file, mode, printSize)
        : await api.comicProcess({ asset_id: assetId, mode, print_size: printSize });
      setResult(r);
    } catch (e) {
      toast({ title: 'Could not process that page', description: e.message });
    } finally {
      setWorking(false);
    }
  };

  const ready = !!file || !!assetId;
  const bestUrl = result?.printUrl || result?.url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#0E0E10]"
           onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center gap-3 border-b border-[#2A2A30] px-5 py-4">
          <BookOpen size={18} className="text-node-product" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-white">Comic restoration</h2>
            <p className="truncate text-xs text-white/45">
              Runs on this machine. The lettering is detected and kept untouched.
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 text-[10px] text-emerald-300">
            No credits · instant
          </span>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-white/40">The scanned page</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => fileRef.current?.click()}
                      data-testid="comic-upload"
                      className={`flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border text-white/40 hover:border-white/40 hover:text-white ${
                        file ? 'border-[#F5A623] ring-2 ring-[#F5A623]/45' : 'border-dashed border-[#2A2A30]'}`}>
                {file && filePreview
                  ? <img src={filePreview} alt="" className="h-full w-full rounded-md object-cover" />
                  : (<><Upload size={16} /><span className="text-[10px]">Upload</span></>)}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                     onChange={(e) => pickFile(e.target.files?.[0])} />
              {assets.slice(0, 15).map((a) => (
                <button key={a.id}
                        onClick={() => { setAssetId(a.id === assetId ? '' : a.id); setFile(null); setResult(null); }}
                        title={a.name}
                        className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-all ${
                          assetId === a.id
                            ? 'border-[#F5A623] ring-2 ring-[#F5A623]/45'
                            : 'border-[#2A2A30] hover:border-white/40'}`}>
                  {a.url
                    ? <img src={fileUrl(a.url)} alt={a.name} className="h-full w-full object-cover" />
                    : <span className="text-[10px] text-white/40">{a.name?.slice(0, 8)}</span>}
                </button>
              ))}
            </div>
            {file && (
              <p className="mt-1.5 text-[11px] text-emerald-300/80">
                {file.name} — will be processed at its original resolution
              </p>
            )}
            {!file && assetId && (
              <p className="mt-1.5 text-[11px] text-white/35">
                Library images are stored at 1280px. For print work, upload the original scan instead.
              </p>
            )}
          </div>

          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-white/40">What to do to it</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {MODES.map((m) => (
                <button key={m.key} onClick={() => { setMode(m.key); setResult(null); }}
                        data-testid={`comic-mode-${m.key}`}
                        className={`rounded-xl border p-3 text-left transition-colors ${
                          mode === m.key
                            ? 'border-white/60 bg-[#141417] text-white'
                            : 'border-[#2A2A30] bg-[#141417] text-white/60 hover:text-white'}`}>
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    {m.label} {mode === m.key && <Check size={13} />}
                  </span>
                  <span className="mt-1 block text-xs text-white/45">{m.hint}</span>
                </button>
              ))}
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2.5 rounded-xl border border-[#2A2A30] bg-[#141417] p-3">
              <input type="checkbox" checked={printSize} data-testid="comic-print-size"
                     onChange={(e) => { setPrintSize(e.target.checked); setResult(null); }}
                     className="h-4 w-4 accent-white" />
              <Printer size={14} className="shrink-0 text-white/50" />
              <span className="text-sm text-white/80">Print size
                <span className="ml-1.5 text-xs text-white/40">also save a 4096px copy, ready to print</span>
              </span>
            </label>
          </div>

          {result && (
            <div>
              <p className="mb-2 text-xs uppercase tracking-wider text-white/40">Result</p>
              <img src={fileUrl(result.url)} alt="Processed page"
                   className="max-h-[40vh] w-full rounded-xl border border-[#2A2A30] object-contain bg-[#141417]" />
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-[11px] text-white/40">
                  {result.meta?.blocks} text blocks kept intact · saved to your Creations
                </p>
                <div className="flex shrink-0 items-center gap-2">
                  <a href={fileUrl(result.url)} download
                     className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 py-1.5 text-xs text-white/80 hover:border-white/40 hover:text-white">
                    <Download size={12} /> Download
                  </a>
                  {result.printUrl && (
                    <a href={fileUrl(result.printUrl)} download
                       className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-medium text-black hover:bg-white/90">
                      <Printer size={12} /> Print size
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <button onClick={run} disabled={!ready || working}
                  data-testid="comic-run"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-40">
            {working ? <Loader2 size={15} className="animate-spin" /> : <BookOpen size={15} />}
            {working ? 'Working on the page… a few seconds'
              : !ready ? 'Pick a page first'
                : result ? 'Run it again' : (mode === 'restore' ? 'Restore this page' : 'Modernize this page')}
          </button>
        </div>
      </div>
    </div>
  );
}
